import React, { FC, useMemo, useState } from 'react';
import { createListCollection, Stack, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { countryTiersOnStage, TIER_MIN_PLAN } from '@simplyanvil/cl-types';
import type { Stage } from '@simplyanvil/cl-types';
import { ALL_COUNTRIES } from '@/data/countries';
import { countryName } from '@/data/plans';
import { currentStage } from '@/libs/stage';
import { SelectContent, SelectItem, SelectRoot, SelectTrigger, SelectValueText } from '@/components/ui/select';

export type FinderResult = {
  iso3: string;
  kind: 'included' | 'requiresPlan' | 'unclassified' | 'comingSoon' | 'unsupported';
  requiredPlan?: 'Plus' | 'Enterprise';
};

export const resolveCountryPlan = (iso3: string, stage: Stage = currentStage()): FinderResult => {
  const entry = countryTiersOnStage(stage)[iso3];
  if (!entry) {
    const anywhere = countryTiersOnStage('dev')[iso3];
    return { iso3, kind: anywhere ? 'comingSoon' : 'unsupported', requiredPlan: undefined };
  }
  if (entry.tier === null) return { iso3, kind: 'unclassified', requiredPlan: undefined };
  const plan = TIER_MIN_PLAN[entry.tier];
  return plan === 'Free' ? { iso3, kind: 'included', requiredPlan: undefined } : { iso3, kind: 'requiresPlan', requiredPlan: plan };
};

interface CountryPlanFinderProps {
  initialCountry?: string;
  compact?: boolean;
  onResolved?: (result: FinderResult) => void;
}

const CountryPlanFinder: FC<CountryPlanFinderProps> = ({ initialCountry, compact, onResolved }) => {
  const { t } = useTranslation('landing');
  const [iso3, setIso3] = useState<string | undefined>(initialCountry);
  const collection = useMemo(
    () => createListCollection({ items: ALL_COUNTRIES.map((c) => ({ label: c.name, value: c.code })) }),
    []
  );
  const result = iso3 ? resolveCountryPlan(iso3) : null;

  return (
    <Stack gap='2' maxW={compact ? 'sm' : 'md'}>
      <Text fontSize='sm' fontWeight='bold'>{t('homepage.pricing.finder.label')}</Text>
      <SelectRoot
        collection={collection}
        value={iso3 ? [iso3] : []}
        onValueChange={({ value }) => {
          setIso3(value[0]);
          if (value[0]) onResolved?.(resolveCountryPlan(value[0]));
        }}
        colorPalette='brand'
      >
        <SelectTrigger>
          <SelectValueText placeholder={t('homepage.pricing.finder.placeholder')} />
        </SelectTrigger>
        <SelectContent>
          {collection.items.map((item) => (
            <SelectItem item={item} key={item.value}>{item.label}</SelectItem>
          ))}
        </SelectContent>
      </SelectRoot>
      {result && (
        <Text fontSize='sm' color='fg.muted'>
          {t(`homepage.pricing.finder.${result.kind}`, { country: countryName(result.iso3), plan: result.requiredPlan })}
        </Text>
      )}
    </Stack>
  );
};

export default CountryPlanFinder;
